import React, { useEffect, useState } from 'react'
import {
  Chip,
  Grid,
  IconButton,
  LinearProgress,
  TextField
} from '@material-ui/core'
import { Autocomplete } from '@material-ui/lab'
import { Add as AddIcon, ChevronRight as ChevronRightIcon, Close as CloseIcon } from '@material-ui/icons'
import { useDebouncedCallback } from 'use-debounce'

import { Place } from 'src/api/models'
import { skuit } from 'src/services/skuit'
import { useQueryPaginator } from 'src/components/Table'
import { FilterProps } from './types'

export const PlaceFilter = (props: FilterProps) => {
  const { label, onChange } = props
  const [place, setPlace] = useState<Place | null>(null)
  const paginator = useQueryPaginator(
    'place-filter',
    skuit.sc.places.list
  )
  const dePaginatorFilters = useDebouncedCallback(paginator.patchFilters, 100)
  return (
    <Autocomplete
      id="place-filter"
      options={paginator.data.results}
      getOptionLabel={(place: Place) => place?.path || ''}
      getOptionSelected={(option, value) => option.guid === value?.guid}
      filterOptions={(option) => option}
      onChange={(event: any, newValue: any | null) => {
        setPlace(newValue)
        onChange(newValue, newValue?.guid)
      }}
      value={place}
      renderInput={(params) => (
        <TextField
          {...params}
          name="place-filter"
          label={label}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            dePaginatorFilters.callback({ search: event.target.value })
          }
        />
      )}
    />
  )
}

export const MultiPlaceFilter = (props: FilterProps) => {
  const { label, defaultValue, onChange } = props
  const [path, setPath] = useState<Place[]>([])
  const [selections, setSelections] = useState<Place[]>(defaultValue || [])

  const paginator = useQueryPaginator(
    'place-filter',
    skuit.sc.places.list
  )
  const dePatch = useDebouncedCallback(paginator.patchFilters, 200)
  const patchFilters = paginator.patchFilters

  const facilityPaginator = useQueryPaginator(
    'facility-filter',
    skuit.sc.facilities.list
  )

  useEffect(() => {
    if (path.length) {
      if (path.length === 1) {
        patchFilters({ facilities: path[0].guid, parents: null, noparent: true, page_size: 100 })
      } else {
        patchFilters({ facilities: null, parents: path[path.length - 1].guid, noparent: null, page_size: 500 })
      }
    }
  }, [path, patchFilters])

  const pg = path.length ? paginator : facilityPaginator

  return (
    <>
      <Grid container>
        <Grid item xs={12}>
          <Autocomplete
            id={label}
            options={pg.data.results}
            getOptionLabel={(obj: Place) => obj.name}
            getOptionSelected={(option, value) => option.guid === value?.guid}
            filterOptions={(option) => option}
            onChange={(_: any, newValue: any | null) => {
              setPath([...path, newValue])
            }}
            value={null}
            renderInput={(params) => (
              <Grid container spacing={2} alignItems="center">
                {path.map((obj, index) => (
                  <React.Fragment key={obj.guid}>
                    <Grid item>
                      <ChevronRightIcon />
                    </Grid>
                    <Grid item>
                      <Chip label={obj.name} onDelete={() => {
                        path.splice(index)
                        setPath([...path])
                      }} />
                    </Grid>
                  </React.Fragment>
                ))}
                <Grid item xs={4}>
                  <TextField
                    {...params}
                    label={pg.loading ? 'Loading...' : label}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                      dePatch.callback({ search: event.target.value })
                    }
                    fullWidth
                  />
                </Grid>
                {path.length > 1 && (<Grid item>
                    <IconButton color="primary" onClick={() => {
                      const place = path[path.length - 1]
                      const newValueList = selections.length ? [...selections, place] : [place]
                      setSelections(newValueList)
                      setPath([])
                      onChange(newValueList, (newValueList.map((place) => place.guid).join(',')))
                    }}>
                      <AddIcon fontSize="small" />
                    </IconButton>
                  </Grid>
                )}
                {pg.loading && <LinearProgress />}
              </Grid>
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <Grid container>
            {selections?.map((obj: any) => {
              if (obj) {
                return (
                  <React.Fragment key={obj.guid}>
                    <Grid item><span>{obj.name}</span></Grid>
                    <Grid item>
                      <IconButton
                        color="primary"
                        onClick={() => {
                          const newValueList = selections.filter((v: any) => v.guid !== obj.guid)
                          setSelections(newValueList)
                          onChange(newValueList, (newValueList.map((place) => place.guid).join(',')))
                        }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Grid>
                  </React.Fragment>
                )
              }
            })}
          </Grid>
        </Grid>
      </Grid>
    </>
  )
}
